<template>
  <el-dialog
    :model-value="modelValue"
    :title="$t('editor.export')"
    :close-on-click-modal="false"
    width="677px"
    :align-center="true"
    class="export-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <!-- 导出次数提示（仅免费用户可见） -->
    <div v-if="userStore.isFreeUser" class="quota-info">
      <span class="quota-text">
        {{ $t('editor.exportRemaining', { count: remainingCount }) }}
      </span>
      <span class="upgrade-link" @click="handleUpgrade">
        <SvgIcon name="editor-upgrade-vip" :size="18" />
        {{ $t('editor.upgradeVip') }}
      </span>
    </div>

    <!-- 导出类型选择卡片 -->
    <div class="export-type-cards">
      <!-- PDF 卡片 -->
      <div
        class="type-card"
        :class="{ selected: exportType === 'pdf' }"
        @click="exportType = 'pdf'"
      >
        <div class="card-icon">
          <SvgIcon name="editor-export-pdf-card" :size="30" />
        </div>
        <span class="card-desc">{{ $t('editor.exportPDFDesc') }}</span>
      </div>

      <!-- JPG 卡片 -->
      <div
        class="type-card"
        :class="{ selected: exportType === 'jpg' }"
        @click="exportType = 'jpg'"
      >
        <div class="card-icon">
          <SvgIcon name="editor-export-jpg-card" :size="30" />
        </div>
        <span class="card-desc">{{ $t('editor.exportJPGDesc') }}</span>
      </div>
    </div>

    <!-- 底部按钮 -->
    <template #footer>
      <div class="dialog-footer">
        <button class="btn-cancel" @click="handleCancel">
          {{ $t('editor.cancel') }}
        </button>
        <button
          class="btn-export"
          :class="{ disabled: userStore.isFreeUser }"
          @click="handleExport"
        >
          {{ $t('editor.export') }}
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
  import { nextTick, ref } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import { emitter } from '@/utils';
  import { EVENT_NAMES } from '@/constants';
  import { useProjectStore, useUserStore } from '@/store';
  import { useUpgradeModal } from '@/hooks';

  export interface ExportDialogProps {
    modelValue: boolean;
    shareLink?: string;
    remainingCount?: number;
  }

  export type ExportType = 'pdf' | 'jpg';

  withDefaults(defineProps<ExportDialogProps>(), {
    shareLink: '',
    remainingCount: 0
  });

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
  }>();

  const projectStore = useProjectStore();
  const userStore = useUserStore();

  const exportType = ref<ExportType>('pdf');

  const { showUpgrade } = useUpgradeModal();

  function handleCancel() {
    emit('update:modelValue', false);
  }

  function handleExport() {
    if (userStore.isFreeUser) {
      showUpgrade();
      return;
    }
    // 先关闭弹窗，等 Vue 渲染完再触发导出事件，
    // 避免导出过程同步阻塞导致弹窗关不掉、loading 卡死
    const type = exportType.value;
    emit('update:modelValue', false);
    nextTick(() => {
      if (type === 'pdf') {
        emitter.emit(EVENT_NAMES.EXPORT_PDF, { filename: projectStore.name ?? '未命名绘本' });
      } else {
        emitter.emit(EVENT_NAMES.EXPORT_JPG, { filename: projectStore.name ?? '未命名绘本' });
      }
    });
  }

  function handleUpgrade() {
    showUpgrade();
  }
</script>

<style scoped>
  /* 内置 el-dialog header 样式覆盖 */
  .export-dialog :deep(.el-dialog__header) {
    padding: 24px 24px 0;
    margin: 0;
  }

  .export-dialog :deep(.el-dialog__title) {
    font-size: 16px;
    font-weight: 600;
    color: #333333;
  }

  .export-dialog :deep(.el-dialog__headerbtn) {
    top: 24px;
    right: 24px;
    width: 24px;
    height: 24px;
    font-size: 16px;
  }

  .export-dialog :deep(.el-dialog__body) {
    padding: 16px 24px 24px;
  }

  .export-dialog :deep(.el-dialog__footer) {
    padding: 0 24px 24px;
  }

  /* 导出次数提示 */
  .quota-info {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    margin-bottom: 12px;
  }

  .quota-text {
    font-size: 13px;
    color: #888888;
    line-height: 18px;
  }

  .upgrade-link {
    display: inline-flex;
    align-items: center;
    box-sizing: border-box;
    height: 24px;
    padding: 3px 11px;
    gap: 2px;
    font-size: 12px;
    font-weight: 400;
    color: #d38743;
    background: #fff8ec;
    border: 1px solid rgba(254, 230, 181, 0.18);
    border-radius: 14px;
    cursor: pointer;
    line-height: 1;
  }

  .upgrade-link:hover {
    opacity: 0.85;
  }

  /* 导出消耗提示 */
  .cost-info {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 12px;
    background: #fbf9ff;
    border-radius: 6px;
    margin-bottom: 16px;
  }

  .cost-text {
    font-size: 14px;
    color: #747474;
    line-height: 22px;
  }

  /* 导出类型卡片 */
  .export-type-cards {
    display: flex;
    gap: 25px;
    margin-bottom: 0;
  }

  .type-card {
    box-sizing: border-box;
    flex: 1;
    height: 230px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    background: #fbf9ff;
    border: 1px solid transparent;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.2s ease;
    padding: 20px;
  }

  .type-card:hover {
    border-color: #d4c8f0;
  }

  .type-card.selected {
    border-color: #6033e1;
    background: #fbf9ff;
  }

  .card-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.75;
  }

  .card-desc {
    font-size: 14px;
    color: #000000;
    line-height: 22px;
    text-align: center;
  }

  /* 底部按钮 */
  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 13px;
  }

  .btn-cancel {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 20px;
    border: 1px solid #c2c2c2;
    border-radius: 6px;
    background: #ffffff;
    cursor: pointer;
    font-size: 14px;
    font-weight: 400;
    color: #000000;
    line-height: 22px;
  }

  .btn-cancel:hover {
    border-color: #a0a0a0;
    background: #f8fafc;
  }

  .btn-export {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 20px;
    border: none;
    border-radius: 6px;
    background: #6033e1;
    cursor: pointer;
    font-size: 14px;
    font-weight: 400;
    color: #ffffff;
    line-height: 22px;
  }

  .btn-export:hover {
    background: #7c5cec;
  }

  .btn-export.disabled {
    background: #c2b4e8;
    cursor: not-allowed;
  }
</style>
